/**
 * Dragging a sheet away dismisses it: down for a bottom sheet, right for the sidebar, from anywhere
 * on it, unless what's under the finger can still scroll back that way or takes typing. Let go past
 * a third of its size, or flicked, and it goes (the sheet's leave animation carries on from where
 * the finger left it); else it springs back. Taps and sideways scrolls stay what they were.
 */
import { useEffect, useRef, type RefObject } from 'react';

/** How far a finger moves before the gesture counts as a drag, in CSS pixels. */
const SLOP = 6;
/** Let go faster than this towards dismissal and it goes, however far it came (px per ms). */
const FLICK = 0.5;
/** Past this share of its size it goes. */
const FAR = 1 / 3;

/** Whether `target`, up to `sheet`, has something scrolled away that a drag should bring back first. */
function scrolledAway(target: EventTarget | null, sheet: HTMLElement, sideways: boolean): boolean {
  for (let n = target instanceof Element ? target : null; n && n !== sheet; n = n.parentElement) {
    if (sideways ? n.scrollLeft > 0 : n.scrollTop > 0) return true;
  }
  return false;
}

const takesTyping = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  target.closest('input, textarea, select, [contenteditable]') !== null;

export function useDragDismiss(
  sheet: RefObject<HTMLElement | null>,
  sidebar: () => boolean,
  onDismiss: () => void,
  enabled: boolean,
): void {
  // the latest callback, without starting over on every render (a drag may outlive one)
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  });
  useEffect(() => {
    const el = sheet.current;
    if (!el || !enabled) return;
    let start: { x: number; y: number; target: EventTarget | null } | null = null;
    let decided = false;
    let dragging = false;
    let sideways = false;
    let offset = 0;
    let lastAt = 0;
    let lastOffset = 0;
    let speed = 0;

    const begin = (x: number, y: number, target: EventTarget | null): void => {
      start = { x, y, target };
      decided = false;
      dragging = false;
      sideways = sidebar();
      offset = 0;
      speed = 0;
    };
    /** Follows the finger; true while this is a dismissing drag (the page must not scroll). */
    const move = (x: number, y: number): boolean => {
      if (!start) return false;
      const along = sideways ? x - start.x : y - start.y;
      const across = sideways ? y - start.y : x - start.x;
      if (!decided) {
        if (Math.abs(along) < SLOP && Math.abs(across) < SLOP) return false;
        decided = true;
        dragging =
          along > 0 &&
          Math.abs(along) > Math.abs(across) &&
          !takesTyping(start.target) &&
          !scrolledAway(start.target, el, sideways);
        if (dragging) el.classList.add('dragging');
      }
      if (!dragging) return false;
      const now = performance.now();
      const next = Math.max(0, along);
      if (now > lastAt) speed = (next - lastOffset) / (now - lastAt);
      lastAt = now;
      lastOffset = next;
      offset = next;
      el.style.transform = sideways
        ? `translateX(${String(offset)}px)`
        : `translateY(${String(offset)}px)`;
      return true;
    };
    /** Let go: dismissed or sprung back. Returns whether it was a drag (its click is not a tap). */
    const end = (): boolean => {
      const was = dragging;
      if (dragging) {
        el.classList.remove('dragging');
        const size = sideways ? el.offsetWidth : el.offsetHeight;
        if (offset > size * FAR || speed > FLICK) dismiss.current();
        else el.style.transform = '';
      }
      start = null;
      dragging = false;
      return was;
    };

    const onTouchStart = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (e.touches.length !== 1 || !touch) return;
      begin(touch.clientX, touch.clientY, e.target);
    };
    const onTouchMove = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (touch && move(touch.clientX, touch.clientY) && e.cancelable) e.preventDefault();
    };
    const onTouchEnd = (): void => {
      end();
    };
    // a mouse drags too (the web on a desktop); its click after a drag is swallowed
    const onMouseMove = (e: PointerEvent): void => {
      move(e.clientX, e.clientY);
    };
    const swallowClick = (e: MouseEvent): void => {
      e.stopPropagation();
      e.preventDefault();
    };
    const onMouseUp = (): void => {
      removeEventListener('pointermove', onMouseMove);
      removeEventListener('pointerup', onMouseUp);
      if (!end()) return;
      addEventListener('click', swallowClick, { capture: true, once: true });
      setTimeout(() => removeEventListener('click', swallowClick, { capture: true }), 0);
    };
    const onPointerDown = (e: PointerEvent): void => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      begin(e.clientX, e.clientY, e.target);
      addEventListener('pointermove', onMouseMove);
      addEventListener('pointerup', onMouseUp);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    el.addEventListener('pointerdown', onPointerDown);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('pointerdown', onPointerDown);
      removeEventListener('pointermove', onMouseMove);
      removeEventListener('pointerup', onMouseUp);
    };
  }, [sheet, sidebar, enabled]);
}
